/**
 * RecurrenceIcon - SVG glyph for recurring schedules (a circular arrow
 * loop). Used by RecurrenceBadge, the Repeats nav entries, and any
 * future surface that needs to denote "this is part of a series".
 *
 * Pure presentational atom — accepts the same size + color contract as
 * the other inline SVG atoms in this codebase so it can be dropped
 * into any layout that already uses Tailwind sizing classes.
 *
 * @example
 * <RecurrenceIcon className="w-14px h-14px text-brand-text-muted" />
 */
import React from 'react';

export interface RecurrenceIconProps {
	/** Tailwind class string (size, color, etc.). Defaults to a 16px muted icon. */
	className?: string;
	/** ARIA label when the icon stands alone (without surrounding text). */
	ariaLabel?: string;
}

export const RecurrenceIcon: React.FC<RecurrenceIconProps> = ({
	className = 'w-16px h-16px text-brand-text-muted',
	ariaLabel,
}) => (
	<svg
		className={`recurrence-icon ${className}`}
		data-ref="atom-recurrence-icon"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.75}
		strokeLinecap="round"
		strokeLinejoin="round"
		role={ariaLabel ? 'img' : undefined}
		aria-label={ariaLabel}
		aria-hidden={ariaLabel ? undefined : true}
	>
		{/* Top-half arc + arrowhead pointing right — "repeat right" */}
		<path d="M4.5 12a7.5 7.5 0 0 1 13.5-4.5" />
		<polyline points="14 4 18 7.5 14 11" />
		{/* Bottom-half arc + arrowhead pointing left — "repeat left" */}
		<path d="M19.5 12a7.5 7.5 0 0 1-13.5 4.5" />
		<polyline points="10 20 6 16.5 10 13" />
	</svg>
);

export default RecurrenceIcon;
